import { HStack, Link, Text, Tooltip } from '@chakra-ui/react';

import { CellContext, createColumnHelper, HeaderContext } from '@tanstack/react-table';

import { format, isToday, isYesterday, parseISO } from 'date-fns';

import {
  Transaction,
  TransactionCategory,
  TransactionStatus,
  ExternalLinkOutline,
} from '@aslanpay/portal-common-lib';

import CurrencyAmount from 'app/components/UI/CurrencyAmount/CurrencyAmount';
import { TransactionActions, TransactionRow } from 'app/components/UI/Table/Table.types';
import TextWithClipboard from 'app/components/UI/TextWithClipboard';

import { TransactionField } from 'app/constants/table';

import { TransactionCategoryLabels, TransactionStatusLabels } from 'app/constants/transactions';

import getTribeAdminLink from 'app/utils/tribe';

function getTransactionColumns(actions: TransactionActions) {
  const columnHelper = createColumnHelper<TransactionRow>();

  const columns = Object.keys(TransactionField).map((field, index) => {
    return columnHelper.accessor(field as keyof TransactionRow, {
      cell: ({ getValue, row }) => {
        const value = getValue() as string;

        switch (field as keyof typeof TransactionField) {
          case 'id':
          case 'externalId':
            return value && <TextWithClipboard textStyle="extraSmallRegular" value={value} />;
          case 'transactionCategory':
            return TransactionCategoryLabels[value as TransactionCategory] || value;
          case 'transactionStatus':
            return TransactionStatusLabels[value as TransactionStatus];
          case 'otherMerchant':
            return value || '-';
          case 'createdAt':
          case 'settledAt':
            return value ? (
              <Tooltip label={value} placement="top">
                <Text>
                  {(() => {
                    const date = parseISO(value);
                    const time = format(date, 'HH:mm');
                    if (isToday(date)) {
                      return `Today · ${time}`;
                    }
                    if (isYesterday(date)) {
                      return `Yesterday · ${time}`;
                    }
                    return `${format(date, 'MMM dd')} · ${time}`;
                  })()}
                </Text>
              </Tooltip>
            ) : (
              ''
            );
          case 'totalAmount': {
            const numericValue = Number(value as unknown as number);
            const direction = row.original?.transactionDirection;
            const currency = row.original?.currency;
            const color = direction === 'DEBIT' ? 'red.500' : 'green.500';
            return (
              <CurrencyAmount
                amount={numericValue}
                currency={currency}
                direction={direction}
                showSign
                textAlign="right"
                color={color}
              />
            );
          }
          case 'balance':
            return (
              <CurrencyAmount
                amount={value as unknown as number}
                currency={row.original?.currency}
                textAlign="right"
              />
            );
          case 'externalCardId':
            return value ? (
              <HStack>
                <Text>{value as string}</Text>
                <Link
                  href={getTribeAdminLink(value as string, 'card')}
                  target="_blank"
                  color="brand.purple"
                  display="flex"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ExternalLinkOutline boxSize="16px" />
                </Link>
              </HStack>
            ) : (
              value
            );
          default:
            return value;
        }
      },
      header: TransactionField[field as keyof typeof TransactionField],
      enableSorting: !['conversionRate', 'payCycleName'].includes(field),
      meta: {
        getHeaderContext: (context: HeaderContext<TransactionRow, unknown>) => {
          if (['totalAmount', 'balance'].includes(context.header.id)) {
            return { style: { justifyContent: 'end' } };
          }
          return undefined;
        },
        getCellContext: (context: CellContext<TransactionRow, unknown>) => {
          const isSelected =
            actions.selectedTransaction?.id === (context.row.original as Transaction)?.id;
          const isFirstColumn = index === 0;
          return {
            onClick: () => actions.select(context.row.original as Transaction),
            style: isSelected
              ? {
                  backgroundColor: 'var(--chakra-colors-purple-50)',
                  ...(isFirstColumn && {
                    borderLeft: '3px solid var(--chakra-colors-brand-purple)',
                  }),
                }
              : undefined,
          };
        },
      },
    });
  });

  return columns;
}

export default getTransactionColumns;
